/**
 * 模块: MarketOverviewTab
 * 职责: 大盘概况Tab组件
 * 限制: <280行
 */

import React from 'react';
import { Card, Row, Col, Statistic, Empty, Spin } from 'antd';
import type { MarketOverview } from '../../../types';

interface MarketOverviewTabProps {
  data: MarketOverview | null;
  loading: boolean;
}

export const MarketOverviewTab: React.FC<MarketOverviewTabProps> = ({
  data,
  loading,
}) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" tip="加载大盘概况..." />
      </div>
    );
  }

  if (!data) {
    return <Empty description="暂无大盘概况数据" />;
  }

  // 计算周均搜索量
  const avgSearchVolume = data.total_search_volume
    ? Math.round((data.total_search_volume || 0) / (data.total_keywords || 1))
    : 0;

  return (
    <div>
      <Card title="📊 核心指标概览" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={4}>
            <Statistic 
              title="总搜索词数" 
              value={data.total_keywords || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={5}>
            <Statistic 
              title="总搜索量" 
              value={data.total_search_volume || 0}
              valueStyle={{ color: '#722ed1' }}
              suffix={
                <span style={{ fontSize: 14, color: '#999' }}>
                  {avgSearchVolume ? `(周均${avgSearchVolume})` : ''}
                </span>
              }
            />
          </Col>
          <Col span={5}>
            <Statistic 
              title="总曝光量" 
              value={data.total_impressions || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={5}>
            <Statistic 
              title="总点击量" 
              value={data.total_clicks || 0}
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col span={5}>
            <Statistic 
              title="总购买量" 
              value={data.total_purchases || 0}
              valueStyle={{ color: '#f5222d' }}
            />
          </Col>
        </Row>
      </Card>

      <Card title="🎯 大盘转化漏斗" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="点击率 (CTR)"
              value={data.market_ctr || 0}
              suffix="%"
              precision={2}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="加购率"
              value={data.market_atc_rate || 0}
              suffix="%"
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="转化率 (CVR)"
              value={data.market_cvr || 0}
              suffix="%"
              precision={2}
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="整体转化率"
              value={data.market_overall_cvr || 0}
              suffix="%"
              precision={2}
              valueStyle={{ color: '#f5222d' }}
            />
          </Col>
        </Row>
      </Card>

      <Card title="📈 流量集中度分析" size="small">
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="Top 10搜索词占比"
              value={data.traffic_concentration?.top_10_share || 0}
              suffix="%"
              precision={2}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Top 20搜索词占比"
              value={data.traffic_concentration?.top_20_share || 0}
              suffix="%"
              precision={2}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="长尾词占比"
              value={data.traffic_concentration?.long_tail_share || 0}
              suffix="%"
              precision={2}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

